import React from "react";
import { Linkedin } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";

const VALUES = [
  {
    title: "Curation over catalogue",
    text: "We would rather show three right options than thirty available ones. Every opportunity on this site has been selected for lifestyle appeal, location strength and long-term ownership potential.",
  },
  {
    title: "Evidence over adjectives",
    text: "Net yields, developer track records and district comparables — our recommendations are built on numbers a sceptic can interrogate.",
  },
  {
    title: "Discretion as standard",
    text: "Many of our clients are private individuals, family offices and developers. Their business remains their own.",
  },
];

const About = () => (
  <div data-testid="about-page">
    <SEO
      title="About ACRE — Al Casa Real Estate | ACRE"
      description="ACRE is a UAE property investment advisory and brokerage firm helping global investors access curated opportunities across Dubai, Abu Dhabi, Ras Al Khaimah and Al Marjan Island."
      path="/about"
      keywords="Al Casa Real Estate, ACRE Dubai, Dubai property investment for UK buyers"
    />
    <PageHero
      eyebrow="About ACRE"
      title="About ACRE — Al Casa Real Estate"
      intro="ACRE is a UAE property investment advisory and brokerage firm helping global investors access curated opportunities across Dubai, Abu Dhabi, Ras Al Khaimah and Al Marjan Island."
      testid="about-hero"
    />

    <section className="pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start mb-24" data-testid="founder-profile">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.3)] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] lg:sticky lg:top-28">
              <img
                src="/images/founder.jpg"
                alt="Vatsal Thakkar, Founder & CEO of Al Casa Real Estate"
                className="w-full h-[520px] lg:h-[620px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10]/55 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-display text-xl text-[#F4F3EF]">Vatsal Thakkar</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6CA65] mt-1">Founder & CEO, Al Casa Real Estate</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="eyebrow mb-4">Founders & Team</p>
            <div className="flex items-center gap-3.5 flex-wrap mb-2">
              <h2 className="font-display text-4xl sm:text-5xl font-light text-[#F4F3EF]">Vatsal Thakkar</h2>
              <a
                href="https://www.linkedin.com/in/vatsi"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Vatsal Thakkar on LinkedIn"
                data-testid="founder-linkedin-link"
                className="social-btn social-li w-9 h-9 rounded-full flex items-center justify-center"
              >
                <Linkedin size={15} className="text-white" fill="currentColor" strokeWidth={0.5} />
              </a>
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#C5A880] mb-5">Founder & CEO, Al Casa Real Estate</p>
            <p className="font-display text-2xl text-[#F4F3EF] font-light leading-snug mb-8">
              Buy-side advisers, always.
            </p>
            <p className="text-[#9DA3AE] font-light leading-relaxed mb-5">
              I founded Al Casa Real Estate on a simple observation: overseas buyers of UAE property deserved the same
              standard of impartial, evidence-led advice they would expect when investing at home. Too many were being
              sold to, when what they needed was to be properly advised. Working between London and the UAE, that
              conviction continues to shape everything ACRE does.
            </p>
            <p className="text-[#9DA3AE] font-light leading-relaxed mb-5">
              My work bridges the two markets I know best — the UK and the Middle East. I split my time between them,
              guiding second-home families, Golden Visa buyers, investors and family offices from London to Mumbai to
              New Jersey into curated opportunities across Dubai, Abu Dhabi, Ras Al Khaimah and Al Marjan Island. The
              brief always starts with the life — or the portfolio — the client is actually trying to build.
            </p>
            <p className="text-[#9DA3AE] font-light leading-relaxed mb-5">
              Off-plan launches, rental strategies, Golden Visa facilitation, development partnerships — each demands a
              different kind of counsel, and I have built ACRE to offer all of it under one roof. That is only possible
              because of close working relationships with the region's leading developers — BNW Developments, Emaar,
              Nakheel, OMNIYAT, Binghatti, Sobha Realty, Aldar, DAMAC and Select Group — giving our clients launch
              allocations and off-market access across the three emirates we cover.
            </p>
            <p className="text-[#9DA3AE] font-light leading-relaxed">
              Whether you are buying your first overseas apartment or structuring a land joint venture, every
              conversation with me begins the same way: privately, honestly, and with the numbers on the table. If the
              right advice is to wait, that is what you will hear.
            </p>
          </Reveal>
        </div>

        <Reveal>
          <p className="eyebrow mb-4">How we think</p>
          <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] mb-12">Three principles</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.12}>
              <div className="card-hover h-full bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-8" data-testid={`about-value-${i + 1}`}>
                <span className="font-display text-5xl text-[#E6CA65]/40 block mb-4">0{i + 1}</span>
                <h3 className="font-display text-2xl text-[#F4F3EF] mb-4">{v.title}</h3>
                <p className="text-sm text-[#9DA3AE] font-light leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand testid="about-cta" title="Meet the team behind the curation." subtitle="Every ACRE engagement begins with a private consultation. We would be glad to hear what you are planning." />
  </div>
);

export default About;
